/**
 * Swordsman (步兵/剑士) - Green armor, pointed helmet, straight sword
 * Chinese ancient infantry with standard military appearance
 *
 * 坐标系统说明 (Y轴向上):
 * - 脚底: Y = 0
 * - 头顶: Y ≈ 0.68
 * - 身体中心: X = 0, Z = 0
 */

import * as THREE from 'three';
import { COLORS } from '../unitConstants';
import { mergeGeometries } from '../unitHelpers';

export function createSwordsmanGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const colors: THREE.Color[] = [];

  // ==================== 头部 ====================
  const head = new THREE.SphereGeometry(0.08, 10, 8);
  head.translate(0, 0.52, 0);
  parts.push(head);
  colors.push(COLORS.skin);

  // ==================== 头盔 ====================
  // 头盔主体（半球形）
  const helmetDome = new THREE.SphereGeometry(
    0.085,
    8,
    6,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.55
  );
  helmetDome.translate(0, 0.54, 0);
  parts.push(helmetDome);
  colors.push(COLORS.swordGreen);

  // 头盔金边
  const helmetRim = new THREE.TorusGeometry(0.082, 0.01, 4, 12);
  helmetRim.rotateX(Math.PI / 2);
  helmetRim.translate(0, 0.54, 0);
  parts.push(helmetRim);
  colors.push(COLORS.gold);

  // 额头装饰板
  const foreheadPlate = new THREE.BoxGeometry(0.06, 0.02, 0.012);
  foreheadPlate.translate(0, 0.52, 0.07);
  parts.push(foreheadPlate);
  colors.push(COLORS.gold);

  // 头盔顶部尖刺
  const spike = new THREE.ConeGeometry(0.015, 0.05, 6);
  spike.translate(0, 0.66, 0);
  parts.push(spike);
  colors.push(COLORS.gold);

  // 尖刺底座
  const spikeBase = new THREE.CylinderGeometry(0.018, 0.015, 0.012, 6);
  spikeBase.translate(0, 0.63, 0);
  parts.push(spikeBase);
  colors.push(COLORS.gold);

  // ==================== 躯干 ====================
  // 上身铠甲
  const torso = new THREE.CylinderGeometry(0.07, 0.08, 0.16, 8);
  torso.translate(0, 0.36, 0);
  parts.push(torso);
  colors.push(COLORS.swordGreen);

  // 胸甲装饰（铜色圆盘）
  const chestPlate = new THREE.CylinderGeometry(0.03, 0.03, 0.012, 8);
  chestPlate.rotateX(Math.PI / 2);
  chestPlate.translate(0, 0.38, 0.072);
  parts.push(chestPlate);
  colors.push(COLORS.bronze);

  // 腰带
  const belt = new THREE.CylinderGeometry(0.082, 0.082, 0.02, 8);
  belt.translate(0, 0.27, 0);
  parts.push(belt);
  colors.push(COLORS.gold);

  // 腰带扣
  const buckle = new THREE.BoxGeometry(0.025, 0.02, 0.012);
  buckle.translate(0, 0.27, 0.08);
  parts.push(buckle);
  colors.push(COLORS.gold);

  // 肩甲
  const leftShoulder = new THREE.BoxGeometry(0.045, 0.018, 0.05);
  leftShoulder.translate(-0.09, 0.42, 0);
  parts.push(leftShoulder);
  colors.push(COLORS.swordGreen);

  const rightShoulder = new THREE.BoxGeometry(0.045, 0.018, 0.05);
  rightShoulder.translate(0.09, 0.42, 0);
  parts.push(rightShoulder);
  colors.push(COLORS.swordGreen);

  // 下摆/裙甲
  const skirt = new THREE.CylinderGeometry(0.08, 0.10, 0.08, 8);
  skirt.translate(0, 0.20, 0);
  parts.push(skirt);
  colors.push(COLORS.swordGreen);

  // ==================== 左臂（自然下垂） ====================
  // 左上臂 - 从肩膀向下，略微向外
  const leftUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
  leftUpperArm.rotateZ(0.15); // 略微向外倾斜
  leftUpperArm.translate(-0.105, 0.38, 0);
  parts.push(leftUpperArm);
  colors.push(COLORS.swordGreen);

  // 左前臂 - 连接上臂
  const leftForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
  leftForearm.rotateZ(0.2);
  leftForearm.translate(-0.115, 0.30, 0);
  parts.push(leftForearm);
  colors.push(COLORS.swordGreen);

  // 左手
  const leftHand = new THREE.SphereGeometry(0.02, 6, 5);
  leftHand.translate(-0.125, 0.24, 0);
  parts.push(leftHand);
  colors.push(COLORS.skin);

  // ==================== 右臂（持剑） ====================
  // 右上臂 - 从肩膀向下，略微向外
  const rightUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
  rightUpperArm.rotateZ(-0.15);
  rightUpperArm.translate(0.105, 0.38, 0);
  parts.push(rightUpperArm);
  colors.push(COLORS.swordGreen);

  // 右前臂 - 连接上臂
  const rightForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
  rightForearm.rotateZ(-0.2);
  rightForearm.translate(0.115, 0.30, 0);
  parts.push(rightForearm);
  colors.push(COLORS.swordGreen);

  // 右手（握剑）
  const rightHandX = 0.125;
  const rightHandY = 0.24;
  const rightHandZ = 0;
  const rightHand = new THREE.SphereGeometry(0.02, 6, 5);
  rightHand.translate(rightHandX, rightHandY, rightHandZ);
  parts.push(rightHand);
  colors.push(COLORS.skin);

  // ==================== 腿部 ====================
  // 左大腿
  const leftThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
  leftThigh.translate(-0.035, 0.12, 0);
  parts.push(leftThigh);
  colors.push(COLORS.swordGreen);

  // 左小腿
  const leftShin = new THREE.CylinderGeometry(0.028, 0.022, 0.07, 6);
  leftShin.translate(-0.035, 0.045, 0);
  parts.push(leftShin);
  colors.push(COLORS.swordGreen);

  // 左脚
  const leftFoot = new THREE.BoxGeometry(0.035, 0.018, 0.05);
  leftFoot.translate(-0.035, 0.009, 0.008);
  parts.push(leftFoot);
  colors.push(COLORS.woodDark);

  // 右大腿
  const rightThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
  rightThigh.translate(0.035, 0.12, 0);
  parts.push(rightThigh);
  colors.push(COLORS.swordGreen);

  // 右小腿
  const rightShin = new THREE.CylinderGeometry(0.028, 0.022, 0.07, 6);
  rightShin.translate(0.035, 0.045, 0);
  parts.push(rightShin);
  colors.push(COLORS.swordGreen);

  // 右脚
  const rightFoot = new THREE.BoxGeometry(0.035, 0.018, 0.05);
  rightFoot.translate(0.035, 0.009, 0.008);
  parts.push(rightFoot);
  colors.push(COLORS.woodDark);

  // ==================== 武器：环首刀（中国古代风格，剑尖朝上） ====================
  const swordTilt = -0.8; // 向右侧倾斜约45度

  // 计算手握剑的起始位置（从手的位置开始）
  const gripOffsetX = Math.sin(-swordTilt) * 0.04;
  const gripOffsetY = Math.cos(-swordTilt) * 0.04;

  // 剑柄（木质包裹）- 紧贴手的位置
  const swordHandle = new THREE.CylinderGeometry(0.016, 0.014, 0.08, 6);
  swordHandle.rotateZ(swordTilt);
  swordHandle.translate(rightHandX + gripOffsetX * 0.3, rightHandY + gripOffsetY * 0.3, rightHandZ);
  parts.push(swordHandle);
  colors.push(COLORS.wood);

  // 剑柄缠绕（装饰）
  const handleWrap = new THREE.TorusGeometry(0.018, 0.004, 4, 6);
  handleWrap.rotateY(Math.PI / 2);
  handleWrap.rotateZ(swordTilt);
  handleWrap.translate(rightHandX + gripOffsetX * 0.2, rightHandY + gripOffsetY * 0.2, rightHandZ);
  parts.push(handleWrap);
  colors.push(COLORS.gold);

  // 环首（中国古代刀剑特有的圆环柄首）
  const pommelRing = new THREE.TorusGeometry(0.02, 0.006, 6, 12);
  pommelRing.rotateY(Math.PI / 2);
  pommelRing.rotateZ(swordTilt);
  const pommelX = rightHandX - gripOffsetX * 0.5;
  const pommelY = rightHandY - gripOffsetY * 0.5;
  pommelRing.translate(pommelX, pommelY, rightHandZ);
  parts.push(pommelRing);
  colors.push(COLORS.gold);

  // 护手（中式方形格挡）
  const guard = new THREE.BoxGeometry(0.06, 0.018, 0.03);
  guard.rotateZ(swordTilt);
  const guardX = rightHandX + gripOffsetX * 1.2;
  const guardY = rightHandY + gripOffsetY * 1.2;
  guard.translate(guardX, guardY, rightHandZ);
  parts.push(guard);
  colors.push(COLORS.bronze);

  // 刀身（钢色，略弯曲的设计用直线模拟）
  const blade = new THREE.BoxGeometry(0.032, 0.26, 0.01);
  blade.rotateZ(swordTilt);
  const bladeX = rightHandX + gripOffsetX * 1.2 + Math.sin(-swordTilt) * 0.15;
  const bladeY = rightHandY + gripOffsetY * 1.2 + Math.cos(-swordTilt) * 0.15;
  blade.translate(bladeX, bladeY, rightHandZ);
  parts.push(blade);
  colors.push(COLORS.steel);

  // 刀背加厚（增加层次感）
  const bladeSpine = new THREE.BoxGeometry(0.012, 0.24, 0.015);
  bladeSpine.rotateZ(swordTilt);
  const spineOffsetX = Math.cos(swordTilt) * 0.012;
  const spineOffsetY = Math.sin(swordTilt) * 0.012;
  bladeSpine.translate(bladeX + spineOffsetX, bladeY - spineOffsetY, rightHandZ);
  parts.push(bladeSpine);
  colors.push(COLORS.iron);

  // 刀尖（锐利三角形）
  const bladeTip = new THREE.ConeGeometry(0.016, 0.045, 4);
  bladeTip.rotateZ(swordTilt);
  const tipX = rightHandX + gripOffsetX * 1.2 + Math.sin(-swordTilt) * 0.30;
  const tipY = rightHandY + gripOffsetY * 1.2 + Math.cos(-swordTilt) * 0.30;
  bladeTip.translate(tipX, tipY, rightHandZ);
  parts.push(bladeTip);
  colors.push(COLORS.steel);

  return mergeGeometries(parts, colors);
}
